import React from 'react'
import { Table } from 'react-bootstrap'
import ScreenshotPlaceholder from '../../../components/ScreenshotPlaceholder'
import { Tip, Warn, Code, ArticleLink, Ext, TemplateBtn } from '../_helpers'

export const SECTION_ARTICLES = [
  {
    id: 'results-energy-flow',
    category: 'results',
    title: 'Die Energiebilanz-Diagramm lesen',
    summary: 'So lesen Sie das stundengenaue Diagramm, das nach einer Optimierung jede Energiequelle und -nutzung anzeigt.',
    body: (
      <>
        <p>
          Das Diagramm <strong>Energiebilanz</strong> erscheint unter der KPI-Zusammenfassung auf der
          Seite „Optimierung". Es ist ein stündliches Flächendiagramm mit einer Datenreihe für jede
          Art von Quelle oder Nutzung, umschaltbar zwischen gemeinschaftsweiter und gebäudebezogener
          Ansicht.
        </p>
        <ScreenshotPlaceholder
          img="energy-flow.png"
          description="The Energy Balance area chart over 24 hours — stacked series for Base Load, Flexible Load, PV Generation (or Rooftop PV), Wind Generation, Grid Import, Surplus, Battery Charge and Battery Discharge."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run. Scroll below the KPI Summary to the Energy Balance chart and capture the full 24-hour area chart."
          caption="Energiebilanz über den optimierten Tag."
        />

        <h5 className="mt-3">Die angezeigten Datenreihen</h5>
        <p>Das Diagramm zeigt bis zu folgende Datenreihen — es erscheinen nur die für Ihre Anlagen relevanten:</p>
        <ul>
          <li><strong>Grundlast</strong> — der feste Verbrauch, der jede Stunde gedeckt werden muss.</li>
          <li><strong>Flexible Last</strong> — Verbrauch, den der Optimierer zeitlich verschieben darf.</li>
          <li><strong>PV-Erzeugung</strong> (gemeinschaftsweit) oder <strong>Dach-PV</strong> (pro Gebäude).</li>
          <li><strong>Zuteilung Gemeinschaftserzeugung</strong> — Anteil der Gemeinschaftserzeugung, der diesem Gebäude zugewiesen wird (nur in der gebäudebezogenen Ansicht).</li>
          <li><strong>Windenergie-Erzeugung</strong> — Ertrag einer Gemeinschafts-Windturbine.</li>
          <li><strong>Netzbezug</strong> — in dieser Stunde aus dem Netz bezogene Energie.</li>
          <li><strong>Überschuss</strong> — Erzeugung, die in dieser Stunde weder verbraucht noch gespeichert wurde (eingespeist oder abgeregelt).</li>
          <li><strong>Batterieladung</strong> und <strong>Batterieentladung</strong> — Beitrag der Batterie je Stunde.</li>
        </ul>

        <h5 className="mt-3">Muster, die Aufschluss geben</h5>
        <ul>
          <li><strong>Hohe PV-Erzeugung mittags + Überschuss-Block</strong> — die Solaranlage hat mehr erzeugt als die Gemeinschaft verbrauchen konnte; ohne eine größere Batterie wird der Rest eingespeist oder abgeregelt.</li>
          <li><strong>PV mittags + Batterieladung zur gleichen Stunde</strong> — der Optimierer speichert den Überschuss für die Abendspitze.</li>
          <li><strong>Batterieentladung abends + geringer Netzbezug</strong> — die Batterie erfüllt ihre Aufgabe; bei Spitzentarifen wird kaum teure Netzenergie benötigt.</li>
          <li><strong>Netzbezug nachts + gleichzeitige Batterieladung</strong> — sinnvoll nur, wenn die Nachttarife sehr günstig sind; andernfalls sollte die Tarifdatei geprüft werden.</li>
        </ul>
        <Tip>Vergleichen Sie dies mit der <ArticleLink to="results-battery">Ladezustands-Kurve der Batterie</ArticleLink> — beide erzählen dieselbe Geschichte aus zwei Perspektiven.</Tip>

        <h5 className="mt-3">Jährliche Energieflüsse (Langfristige Planung)</h5>
        <p>
          Ein anderes Diagramm mit ähnlichem Namen erscheint in den Ergebnissen der <em>Langfristigen Planung</em> —
          das Balkendiagramm <strong>Jährliche Energieflüsse</strong>. Dort steht jeder Balken für ein Jahr der
          Projektion: positive Werte = verbrauchte oder bezogene Energie, negative Werte = ins Netz
          eingespeiste Energie. Nutzen Sie es, um zu sehen, wie sich der Energiemix über die Lebensdauer der
          Investition verändert.
        </p>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-battery',
    category: 'results',
    title: 'Die Batterieergebnisse lesen',
    summary: 'Was die Ladezustands-Kurve und die Lade-/Entlade-Datenreihen der Batterie aussagen.',
    body: (
      <>
        <p>
          Wenn eine Batterie Teil Ihrer Gemeinschaft ist, erzeugt der Optimierer eine
          {' '}<strong>Batterie-Fahrplan</strong>-Ansicht mit der <strong>Ladezustands-Kurve (SOC)</strong>,
          die anzeigt, wie voll die Batterie in jeder Stunde ist, sowie die zugehörigen Lade- und
          Entlade-Datenreihen im <ArticleLink to="results-energy-flow">Energiebilanz-Diagramm</ArticleLink>.
        </p>
        <ScreenshotPlaceholder
          img="battery-soc.png"
          description="The state-of-charge curve — 0% to 100% on the Y axis, hours on the X axis, typically a daily up-down pattern between the configured Min SOC and Max SOC bounds (defaults 10% and 90%)."
          source="Platform — Optimization page (titled 'Modelling'), after a successful run on a community that has a battery asset. Scroll to the Battery Schedule section and capture the SOC line chart."
          caption="Ladezustand über den optimierten Tag."
        />

        <h5 className="mt-3">Ein gesundes Tagesmuster</h5>
        <ul>
          <li>Der SOC steigt in sonnigen Stunden (Laden aus PV) oder während günstiger Tarifzeiten.</li>
          <li>Der SOC sinkt in der Abendspitze (Entladen in Lasten, Verdrängung teurer Netzimporte).</li>
          <li>Der SOC bleibt innerhalb der konfigurierten Grenzen — Standardwerte sind <strong>Min 10%</strong> und
            <strong> Max 90%</strong>, eingestellt am Batterie-Asset.</li>
          <li>Die Kurve sollte nicht viele Stunden lang flach am Minimum oder Maximum liegen — das
            deutet darauf hin, dass die Batterie entweder zu klein oder zu wenig genutzt ist.</li>
        </ul>

        <h5 className="mt-3">Zu prüfende Batterieeinstellungen (am Asset)</h5>
        <p>
          Das Verhalten der Batterie bei der Optimierung wird durch folgende Einstellungen auf der
          Gemeinschafts- oder Gebäude-Asset-Seite bestimmt:
        </p>
        <ul>
          <li><strong>Kapazität (kWh)</strong> — wie viel Energie hineinpasst.</li>
          <li><strong>Lade-/Entladeleistung (kW)</strong> — wie schnell Energie ein- und ausgespeichert werden kann.</li>
          <li><strong>Wirkungsgrad (Hin- und Rückweg) (%)</strong> — Energie aus geteilt durch Energie ein. Standard
            ist 90 %. Realistisches Li-Ionen liegt bei 85–95 % — deutlich niedrigere Werte bedeuten, dass Verluste
            aggressiv modelliert werden.</li>
          <li><strong>Min-/Max-SOC (%)</strong> — das Betriebsfenster, Standard 10 % bis 90 %.</li>
          <li><strong>Anfangs-SOC (%)</strong> — Ausgangszustand der Batterie (Standard 50 %).</li>
        </ul>

        <h5 className="mt-3">Fahrplan und SOC gemeinsam lesen</h5>
        <p>
          Vergleichen Sie die SOC-Kurve mit den Datenreihen Batterieladung / Batterieentladung im
          {' '}<ArticleLink to="results-energy-flow">Energiebilanz-Diagramm</ArticleLink>: Jede Stunde
          „Laden" sollte den SOC anheben, jede Stunde „Entladen" ihn senken. Ein SOC-Sprung ohne
          entsprechende Ladung im Fahrplan bedeutet, dass die Daten zwischen den Ansichten geändert
          wurden — das sollte in einem einzelnen Lauf nicht vorkommen.
        </p>
        <Warn>
          Eine Batterie, die viele Stunden bei 100 % (oder ihrem Max-SOC) verharrt, ist nutzlos — entweder
          ist die PV-Anlage im Verhältnis zur Last überdimensioniert, der Tarif bietet keinen nutzbaren
          Preisunterschied, oder das Ziel bzw. die Nebenbedingungen verhindern die Entladung. Versuchen
          Sie das Ziel <em>Kosten minimieren</em> mit einem echten Day-Ahead-Tarif, um zu sehen, ob die
          Batterie aktiver wird.
        </Warn>
      </>
    ),
  },

  // =========================================================================
  {
    id: 'results-cba',
    category: 'results',
    title: 'Die Ergebnisse der Langfristigen Planung (CBA) lesen',
    summary: 'NPV, BCR, Amortisationszeit, jährliche Einsparungen, CO₂ — was jede Kennzahl bedeutet und wie Szenarien verglichen werden.',
    body: (
      <>
        <p>
          Die <strong>Langfristige Planung</strong> vergleicht eines oder mehrere Investitionsszenarien (neue PV-Anlage,
          neue Batterie, neue EV-Ladepunkte usw.) mit dem <em>Basisszenario</em> (keine Maßnahmen)
          über einen von Ihnen gewählten Zeitraum (typischerweise 5, 10 oder 20 Jahre). Das Ergebnis
          ist eine vergleichende Tabelle finanzieller KPIs sowie Diagramme.
        </p>
        <ScreenshotPlaceholder
          img="cba-scenarios.png"
          description="The CBA scenario comparison — one row per scenario showing NPV, BCR, payback, annual savings, and CO₂ avoided per year. The best-financial scenario is highlighted, and the best-environmental scenario (if different) is badged."
          source="Platform — open a community → click 'Past Results' (or Planning Tools) → run a Long-Term Planning scenario → open its results. Capture the scenario comparison panel near the top of the page."
          caption="Szenariovergleich aus der Langfristigen Planung."
        />

        <h5 className="mt-3">Was jede Spalte bedeutet</h5>
        <Table bordered size="sm" responsive className="small">
          <thead style={{ background: 'var(--color-neutral-100)' }}>
            <tr><th>Spalte</th><th>Bedeutung in einfacher Sprache</th><th>Gut wenn…</th></tr>
          </thead>
          <tbody>
            <tr><td><strong>NPV</strong></td><td>Gesamtgewinn (in heutigem Geldwert) über die Projektlaufzeit nach Anwendung des Diskontsatzes.</td><td>Positiv (und so hoch wie möglich).</td></tr>
            <tr><td><strong>BCR</strong></td><td>Nutzen-Kosten-Verhältnis: Gesamtnutzen geteilt durch Gesamtkosten.</td><td>Über 1,0.</td></tr>
            <tr><td><strong>Amortisationszeit (Jahre)</strong></td><td>Jahre, bis die kumulierten Einsparungen die Anfangskosten decken.</td><td>Kürzer als die Lebensdauer des Assets (z. B. Solaranlage hält 20–25 Jahre).</td></tr>
            <tr><td><strong>Jährliche Einsparungen</strong></td><td>Durchschnittliche Einsparungen pro Jahr gegenüber dem Basisszenario (oder jährliche Netzkosten für die Basiszeile).</td><td>Je höher, desto besser — aber stets mit den Anfangskosten vergleichen.</td></tr>
            <tr><td><strong>Vermiedene CO₂ / Jahr</strong></td><td>Tonnen CO₂, die pro Jahr gegenüber dem Basisszenario nicht ausgestoßen werden (verwendet den CO₂-Faktor Ihres Gemeinschaftsnetzes).</td><td>Größer ist besser; nützlich für Nachhaltigkeitsberichte.</td></tr>
          </tbody>
        </Table>

        <h5 className="mt-3">Hinweis zum Diskontsatz</h5>
        <p>
          Die Seite bietet ein Eingabefeld für den <strong>Diskontsatz</strong> (Standard 8 %) sowie eine
          Sensitivitätstabelle. Der NPV wird für den eingestellten Satz berechnet. Versuchen Sie, ihn
          um ±2 Prozentpunkte zu verschieben, um zu sehen, wie empfindlich Ihre Schlussfolgerung
          reagiert — siehe die Tabelle <em>Diskontsatz-Sensitivität</em> auf derselben Seite.
        </p>

        <h5 className="mt-3">Szenarien vergleichen</h5>
        <ol>
          <li>Beginnen Sie mit dem <strong>NPV</strong> — er ist die beste finanzielle Zusammenfassung. Das Szenario mit dem höchsten NPV ist der finanzielle Gewinner und wird auf der Seite hervorgehoben.</li>
          <li>Prüfen Sie mit <strong>BCR</strong> und <strong>Amortisationszeit</strong> — wenn der NPV hoch ist, die Amortisationszeit aber 18 Jahre bei einem Asset mit 20 Jahren Lebensdauer beträgt, ist das Szenario anfällig gegenüber Annahmen.</li>
          <li>Wenn zwei Szenarien beim NPV gleichauf liegen, bevorzugen Sie den höheren BCR oder die geringeren Anfangskosten.</li>
          <li>Vergleichen Sie <strong>Vermiedene CO₂</strong> separat — das beste ökologische Szenario kann vom besten finanziellen abweichen, und die Seite kennzeichnet es entsprechend.</li>
        </ol>

        <Warn>
          Ein positiver NPV hängt von den Eingabeannahmen ab — Strompreissteigerung, Diskontsatz,
          Asset-Lebensdauer. Verwenden Sie die Sensitivitätstabelle auf der Seite, um zu prüfen, ob
          der NPV bei einer moderaten Änderung des Diskontsatzes positiv bleibt; kippt er leicht ins
          Negative, ist das Szenario nicht robust.
        </Warn>
        <Tip>
          Die Ergebnisseite der Plattform zeigt das beste finanzielle und das beste ökologische Szenario
          als zusammenfassende Hervorhebungen. Nutzen Sie diese als schnellen Überblick, prüfen Sie aber
          stets die vollständige Tabelle, bevor Sie eine Investition empfehlen.
        </Tip>
      </>
    ),
  },
]
